import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings, AlertTriangle, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const MercedesService = () => {
  const mercedesFeatures = [
    'Mercedes-Benz Specialists',
    'STAR Diagnostic Equipment',
    'OEM Mercedes Parts',
    'AMG Performance Service',
    'All Mercedes Models',
    '12-Month/12,000-Mile Warranty'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const commonIssues = [
    { 
      model: 'E-Class (W211/W212)', 
      years: '2003-2016',
      issues: ['Airmatic suspension failures', 'Balance shaft problems', 'SBC brake pump issues'],
      solution: 'Complete diagnostic and OEM parts replacement'
    },
    {
      model: 'C-Class (W204/W205)',
      years: '2008-Present',
      issues: ['Steering lock failures', 'Timing chain stretch', 'Transmission conductor plate'],
      solution: 'Preventive maintenance and early intervention'
    },
    {
      model: 'S-Class (W220/W221)',
      years: '2000-2013',
      issues: ['ABC hydraulic system', 'Air suspension compressor', 'Command system failures'],
      solution: 'Specialized hydraulic and electronic repair'
    },
    {
      model: 'ML/GLE-Class',
      years: '2006-Present',
      issues: ['Transfer case issues', '7G-Tronic transmission', 'AdBlue system problems'],
      solution: 'Advanced diagnostic and component replacement'
    }
  ];

  const services = [
    {
      title: 'Maintenance Services',
      items: [
        'Service A & B Maintenance',
        'Oil & Filter Changes',
        'Brake Fluid Exchange',
        'Transmission Service',
        'Differential Service'
      ]
    },
    {
      title: 'Diagnostic & Repair',
      items: [
        'STAR Diagnostic System',
        'Check Engine Light',
        'Electrical Troubleshooting',
        'Module Programming',
        'Software Updates'
      ]
    },
    {
      title: 'Specialized Services',
      items: [
        'Airmatic Suspension',
        'ABC Hydraulic System',
        'AMG Performance Service',
        'BlueTEC Diesel Service',
        'Hybrid System Repair'
      ]
    },
    {
      title: 'Performance',
      items: [
        'AMG Upgrades',
        'Exhaust Systems',
        'Suspension Tuning',
        'ECU Remapping',
        'Brake Upgrades'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Mercedes-Benz Repair Dallas | German Auto Specialists Paradise TX | Paradise-Performance</title>
        <meta name="description" content="Mercedes-Benz repair and service for Dallas-Fort Worth. STAR diagnostics, AMG performance, AIRMATIC suspension experts. Save 30-40% vs dealer. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/european-car-repair/mercedes" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/european-car-repair/mercedes" />
        <meta property="og:title" content="Mercedes-Benz Repair Dallas | Expert German Auto Service" />
        <meta property="og:description" content="Dallas Mercedes specialists. STAR diagnostics, AIRMATIC, AMG experts. 30-40% less than dealership. Call (817) 382-2316." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/mercedes-repair.webp" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mercedes-Benz Repair Dallas | Expert German Auto Service" />
        <meta name="twitter:description" content="Dallas Mercedes specialists. STAR diagnostics, AIRMATIC, AMG experts. 30-40% less than dealership." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/mercedes-repair.webp" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Mercedes Repair Dallas - Paradise-Performance",
            "description": "Mercedes repair Dallas specialists. Expert Mercedes-Benz service for Dallas-Fort Worth. STAR diagnostics, AMG performance, AIRMATIC experts.",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": ["Dallas", "Fort Worth", "Highland Park", "University Park", "North Dallas", "Richardson", "Plano", "Frisco"],
            "brand": "Mercedes-Benz",
            "priceRange": "$$$",
            "openingHours": "Mo-Fr 09:00-17:00,Sa 10:00-14:00"
          })}
        </script>

        {/* FAQ Schema for Featured Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does Mercedes-Benz maintenance cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Mercedes-Benz maintenance costs average $1,200 to $1,800 per year depending on the model. Service A runs $200-$350 at an independent shop (vs $400-$600 at dealerships). Service B costs $350-$550 independently. AIRMATIC suspension repairs range from $800-$2,500. At Paradise-Performance near Dallas, we're typically 30-40% less than Mercedes dealership prices."
                }
              },
              {
                "@type": "Question",
                "name": "What is the Mercedes Service A and B schedule?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Mercedes uses a flexible service system alternating between Service A and Service B approximately every 10,000 miles or 12 months. Service A includes oil change, filter, fluid top-off, and inspection. Service B adds cabin filter, brake fluid flush, and more comprehensive inspection. The ASSYST Plus system in your Mercedes calculates the optimal interval."
                }
              },
              {
                "@type": "Question",
                "name": "Are Mercedes expensive to maintain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Mercedes-Benz vehicles cost more to maintain than mainstream brands, averaging $1,200-$1,800 annually. However, using an independent Mercedes specialist instead of the dealership can reduce costs by 30-40%. Common expensive repairs include AIRMATIC suspension ($1,500-$3,000), ABC hydraulic system ($2,000-$5,000), and transmission conductor plates ($800-$1,500)."
                }
              },
              {
                "@type": "Question",
                "name": "What are common Mercedes-Benz problems?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Common Mercedes issues include: AIRMATIC air suspension failures (compressor, struts, valve block), SBC brake pump failure (W211 E-Class), balance shaft wear (M272/M273 V6/V8), transmission conductor plate issues (7G-Tronic), and steering lock module failure (older models). Regular maintenance with MB 229.5 spec oil helps prevent many engine issues."
                }
              },
              {
                "@type": "Question",
                "name": "How long is the Mercedes-Benz warranty?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "New Mercedes-Benz vehicles come with a 4-year/50,000-mile limited warranty. Powertrain is included. Rust perforation is covered for 4 years with unlimited mileage. Mercedes also offers extended warranty options. Certified Pre-Owned Mercedes vehicles get an additional 1-year/unlimited mileage warranty."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Mercedes-Benz"
        brandColor="#00D2BE"
        tagline="Precision Service Excellence"
        description="Paradise-Performance delivers dealership-quality Mercedes-Benz service with the personal touch and fair pricing you deserve."
        features={mercedesFeatures}
        heroImage="/assets/assets/images/mercedes-repair.webp"
        heroImageAlt="Mercedes-Benz AMG GT Performance"
        heroVariant="default"
      />

      {/* Main Content */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          
          {/* Emergency Box */}
          <motion.div {...fadeInUp} className="bg-gray-100 border-2 border-gray-700 rounded-lg p-6 max-w-4xl mx-auto mb-12">
            <h3 className="text-2xl font-bold text-gray-800 mb-2 text-center">⚡ Mercedes Service Available Today</h3>
            <p className="text-center text-gray-700 mb-4">
              Got a check engine light? AIRMATIC problems? Call <strong>(817) 382-2316</strong> now
            </p>
            <div className="text-center">
              <Link to="/contact" className="inline-block bg-gray-800 text-white px-6 py-3 rounded-lg font-bold hover:bg-gray-900 transition">
                Schedule Mercedes Service →
              </Link>
            </div>
          </motion.div>

          {/* Introduction */}
          <motion.div {...fadeInUp} className="mb-16">
            <h1 className="text-4xl font-bold text-gray-900 mb-6 text-center">Mercedes-Benz Repair in Dallas-Fort Worth</h1>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <img 
                  src="/assets/assets/images/new/euro-repair-img/mercedes.webp" 
                  alt="Mercedes-Benz service in Paradise, TX"
                  className="rounded-lg shadow-lg w-full h-auto"
                />
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="text-2xl font-bold mb-4">Real Mercedes Expertise, Not Just Talk</h2>
                <p className="text-gray-700 mb-4">
                  Look, we get it. Your Mercedes wasn't cheap, and neither should the service be cheap quality. 
                  But that doesn't mean you need to pay dealer prices either. We've been working on Mercedes-Benz 
                  vehicles for over 20 years here in Paradise, right outside Dallas.
                </p>
                <p className="text-gray-700 mb-4">
                  We've seen it all - AIRMATIC suspensions that drop overnight, balance shaft issues that'll 
                  cost you an engine if ignored, and those annoying conductor plate failures in the transmission. 
                  The difference? We actually fix them right the first time.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />20+ Years Mercedes Experience</li>
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />STAR Diagnostic Equipment</li>
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />OEM Parts Available</li>
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />12-Month/12,000-Mile Warranty</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Service Cards */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mercedes-Benz Services We Provide</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {/* Service A/B */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <Settings className="w-8 h-8 text-gray-800 mr-3" />
                  <h3 className="text-xl font-bold">Service A/B Maintenance</h3>
                </div>
                <p className="text-gray-700 mb-4">Keep your warranty valid and your Mercedes running smooth</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Synthetic oil (MB 229.5 spec)</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />All filters replaced</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Brake fluid check</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Tire rotation</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Reset service indicator</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Full inspection report</li>
                </ul>
              </div>

              {/* Suspension */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <Car className="w-8 h-8 text-gray-800 mr-3" />
                  <h3 className="text-xl font-bold">AIRMATIC & Suspension</h3>
                </div>
                <p className="text-gray-700 mb-4">Dallas roads are rough on air suspension systems</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />AIRMATIC compressor repair</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Air strut replacement</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />ABC hydraulic service</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Valve block repairs</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Level sensor calibration</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Conversion options available</li>
                </ul>
              </div>

              {/* AMG Service */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <Award className="w-8 h-8 text-red-600 mr-3" />
                  <h3 className="text-xl font-bold">AMG Performance</h3>
                </div>
                <p className="text-gray-700 mb-4">Your AMG deserves specialized attention</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />AMG engine service</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Performance brakes</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Differential service</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Exhaust systems</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Performance tuning</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Track prep service</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Mercedes Maintenance Cost Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Mercedes-Benz Maintenance Cost in Dallas</h2>
            <p className="text-gray-700 mb-6">
              Mercedes service at the dealership is expensive. Here's what you'll actually pay at our shop vs taking it to Park Place or Mercedes of Dallas:
            </p>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Service</th>
                    <th className="px-6 py-4 text-left">Dealership Price</th>
                    <th className="px-6 py-4 text-left">Our Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Service A (Oil, Filter, Inspection)</td>
                    <td className="px-6 py-4 text-gray-600">$400 - $600</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$200 - $350</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Service B (Full Service)</td>
                    <td className="px-6 py-4 text-gray-600">$600 - $900</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$350 - $550</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Brake Pads & Rotors (Front)</td>
                    <td className="px-6 py-4 text-gray-600">$900 - $1,400</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$550 - $850</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">AIRMATIC Strut Replacement</td>
                    <td className="px-6 py-4 text-gray-600">$2,500 - $3,500</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$1,500 - $2,200</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Transmission Service (7G-Tronic)</td>
                    <td className="px-6 py-4 text-gray-600">$600 - $900</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$350 - $500</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Conductor Plate Replacement</td>
                    <td className="px-6 py-4 text-gray-600">$1,800 - $2,500</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$800 - $1,500</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-600 text-sm italic">
              * Prices vary by model. AMG and S-Class models cost more. These are ballpark figures for C-Class, E-Class, and GLE models.
            </p>
          </motion.div>

          {/* Common Mercedes Problems */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mercedes Problems Every Dallas Owner Should Know</h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-10">
              After fixing thousands of Mercedes in the Dallas area, we've noticed patterns. Here's what goes wrong 
              most often and why catching it early saves you big money.
            </p>

            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-start mb-4">
                  <AlertTriangle className="w-8 h-8 text-orange-500 mr-4 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">AIRMATIC Suspension Goes Flat Overnight</h3>
                    <p className="text-gray-700 mb-4">
                      Wake up to find your S-Class, E-Class, or GL sitting on the ground? That's AIRMATIC failure. 
                      The Texas heat destroys the rubber air bladders faster than Mercedes expected. We see this 
                      constantly on 2007-2014 models especially.
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="font-bold text-blue-700 mb-2">What We Do:</p>
                      <p className="text-gray-700">
                        First, we check if it's just a leaky air strut or if the compressor's burned out from overworking. 
                        Sometimes it's just a $50 relay. Other times you need new struts. We'll give you honest options - 
                        including conversion to regular shocks if you're tired of the air ride headaches.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-start mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-500 mr-4 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Balance Shaft Failure (M272/M273 Engines)</h3>
                    <p className="text-gray-700 mb-4">
                      Got a 2006-2012 with a V6 or V8? Listen for a rattle on startup. That's your balance shaft 
                      gear wearing out. Ignore it and you're looking at a $15,000 engine replacement. Mercedes had 
                      a lawsuit about this - they knew it was a problem.
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="font-bold text-blue-700 mb-2">Prevention is Key:</p>
                      <p className="text-gray-700">
                        We can check the gear wear with a borescope - no need to tear apart the engine just to look. 
                        If caught early, we replace the gears and chain for about $3,500. Wait too long? You'll need 
                        a new engine. We check this on every M272/M273 that comes in.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-start mb-4">
                  <AlertTriangle className="w-8 h-8 text-yellow-500 mr-4 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">7G-Tronic Transmission Acting Weird</h3>
                    <p className="text-gray-700 mb-4">
                      Harsh shifts? Won't go into gear? Stuck in limp mode? That's usually the conductor plate 
                      inside the transmission going bad. It's basically a circuit board swimming in transmission 
                      fluid - what could go wrong, right?
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="font-bold text-blue-700 mb-2">The Fix:</p>
                      <p className="text-gray-700">
                        Good news - we don't need to rebuild your whole transmission. We drop the pan, replace the 
                        conductor plate and connector, change the fluid and filter, and reprogram it. Takes about 
                        half a day and costs way less than the $6,000 rebuild the dealer will try to sell you.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Additional Image Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/euro-repair-img/pp-logo-nbg.webp"
                  alt="Mercedes diagnostics"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-bold text-sm">Expert Diagnostics</p>
                </div>
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/euro-repair-img/bmw-valve-cover-gasket.webp"
                  alt="Mercedes engine service"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-bold text-sm">Engine Service</p>
                </div>
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/euro-repair-img/bmw-turbo-leak.webp"
                  alt="Mercedes performance upgrades"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-bold text-sm">Performance Upgrades</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Mercedes Models */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mercedes-Benz Models We Service</h2>
            <div className="bg-gray-100 rounded-lg p-8">
              <div className="grid md:grid-cols-4 gap-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Sedans</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• C-Class (C300, C43, C63)</li>
                    <li>• E-Class (E350, E450, E63)</li>
                    <li>• S-Class (S500, S560, S63)</li>
                    <li>• CLA-Class</li>
                    <li>• CLS-Class</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">SUVs</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• GLA, GLB</li>
                    <li>• GLC (300, 43, 63)</li>
                    <li>• GLE (350, 450, 63)</li>
                    <li>• GLS (450, 580, 63)</li>
                    <li>• G-Wagon (G550, G63)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Coupes & Convertibles</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• C-Class Coupe/Cabrio</li>
                    <li>• E-Class Coupe/Cabrio</li>
                    <li>• SL Roadster</li>
                    <li>• AMG GT</li>
                    <li>• SLC (formerly SLK)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Specialty</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• All AMG Models</li>
                    <li>• Maybach</li>
                    <li>• EQS Electric</li>
                    <li>• Sprinter Vans</li>
                    <li>• Classic Mercedes</li>
                  </ul>
                </div>
              </div>
              <p className="text-center text-gray-600 mt-6 italic">
                Whether it's a 1980s diesel that won't die or a brand new AMG, we've got you covered
              </p>
            </div>
          </motion.div>

          {/* Testimonials */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Dallas Mercedes Owners Trust Us</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  "My GL450 was at the dealer three times for AIRMATIC problems. They wanted to replace everything 
                  for $14,000. Paradise-Performance found it was just a bad height sensor and fixed it for under $800. 
                  Been perfect for six months now."
                </p>
                <p className="font-bold">— Robert K., GL450 Owner</p>
                <p className="text-sm text-gray-600">Highland Park, Dallas</p>
              </div>
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  "They caught the balance shaft issue on my E350 before it destroyed the engine. Saved me from 
                  a $15,000 nightmare. Now I bring both my Mercedes here. Honest, skilled, and they actually 
                  answer the phone when you call."
                </p>
                <p className="font-bold">— Lisa M., E350 & C300 Owner</p>
                <p className="text-sm text-gray-600">Plano, TX</p>
              </div>
            </div>
          </motion.div>

          {/* FAQ */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mercedes Service Questions</h2>
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3">Do you really save me money vs the dealership?</h3>
                <p className="text-gray-700">
                  Yes, typically 30-40% less. But here's the real savings - we don't upsell you stuff you don't need. 
                  Dealer says you need new brake pads at 50% life? We'll show you exactly how much is left and let 
                  you decide. Plus, we don't charge $200 just to look at your car.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3">Can you really fix AIRMATIC suspension?</h3>
                <p className="text-gray-700">
                  Absolutely. We repair AIRMATIC systems every week. Sometimes it's a simple fix like a relay or 
                  level sensor. Sometimes you need new struts. We can also convert to regular suspension if you're 
                  done with the air ride problems. We'll diagnose it properly first, then give you options.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3">Do you work on AMG models?</h3>
                <p className="text-gray-700">
                  Yes, AMGs are actually some of our favorite cars to work on. From the twin-turbo V8s to the 
                  hand-built engines, we understand what makes them special. We do everything from regular service 
                  to performance upgrades and track prep.
                </p>
              </div>
            </div>
          </motion.div>

          {/* AMG Specialist Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-black rounded-xl overflow-hidden">
              <div className="p-8">
                <h3 className="text-3xl font-bold text-white mb-4">AMG Performance Specialists</h3>
                <p className="text-gray-300 mb-6">
                  Our technicians are specially trained in AMG performance vehicles, understanding the unique 
                  requirements of these high-performance machines. From routine maintenance to performance 
                  upgrades, we ensure your AMG delivers the driving experience it was designed for.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-900 rounded p-4">
                    <h4 className="text-[#00D2BE] font-semibold mb-2">AMG Services</h4>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li>• Performance brake service</li>
                      <li>• Suspension tuning</li>
                      <li>• Exhaust upgrades</li>
                      <li>• ECU optimization</li>
                    </ul>
                  </div>
                  <div className="bg-gray-900 rounded p-4">
                    <h4 className="text-[#00D2BE] font-semibold mb-2">Models Serviced</h4>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li>• C63, E63, S63 AMG</li>
                      <li>• GLC63, GLE63 AMG</li>
                      <li>• GT, GT S, GT R</li>
                      <li>• All AMG variants</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Other European Vehicles */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">We Also Service Other European Brands</h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <Car className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">BMW</h3>
                <p className="text-sm text-gray-600 mb-3">Ultimate driving machines</p>
                <Link to="/services/european-car-repair/bmw" className="text-blue-600 hover:underline">View BMW Service →</Link>
              </div>
              <div className="text-center">
                <Car className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">Audi</h3>
                <p className="text-sm text-gray-600 mb-3">Quattro specialists</p>
                <Link to="/services/european-car-repair/audi" className="text-red-600 hover:underline">View Audi Service →</Link>
              </div>
              <div className="text-center">
                <Car className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">Volkswagen</h3>
                <p className="text-sm text-gray-600 mb-3">German engineering</p>
                <Link to="/services/european-car-repair/volkswagen" className="text-blue-500 hover:underline">View VW Service →</Link>
              </div>
              <div className="text-center">
                <Car className="w-12 h-12 text-red-700 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">Porsche</h3>
                <p className="text-sm text-gray-600 mb-3">Performance focused</p>
                <Link to="/services/european-car-repair/porsche" className="text-red-700 hover:underline">View Porsche Service →</Link>
              </div>
            </div>
          </motion.div>

          {/* Final CTA Section */}
          <motion.div {...fadeInUp} className="text-center bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-12 text-white">
            <h2 className="text-4xl font-bold mb-4">Get Your Mercedes Running Right</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Stop overpaying at the dealer. Get honest Mercedes service from mechanics who actually know 
              these cars inside and out. We're here when you need us.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-gray-800 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition"
              >
                Schedule Mercedes Service
              </Link>
              <a
                href="tel:8173822316"
                className="bg-[#00D2BE] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#00A8A3] transition"
              >
                Call Now (817) 382-2316
              </a>
            </div>
            <p className="mt-6 text-lg">Paradise, TX • Serving All of Dallas-Fort Worth</p>
          </motion.div>

          {/* Related Services - Internal Linking for SEO */}
          <motion.div {...fadeInUp} className="mt-12 border-t border-gray-200 pt-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Complete European Auto Repair Dallas Services</h3>
            <p className="text-gray-700 mb-6">
              Looking for <Link to="/services/european-car-repair" className="text-[#00D2BE] hover:underline font-semibold">European auto repair in Dallas</Link>? 
              We service all premium European brands with the same expertise and factory-level diagnostics.
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/services/european-car-repair/porsche" className="text-[#00D2BE] hover:underline">Porsche Repair Dallas</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/european-car-repair/audi" className="text-[#00D2BE] hover:underline">Audi Repair Dallas</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/oil-change" className="text-[#00D2BE] hover:underline">Mercedes Oil Change</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/transmission-repair" className="text-[#00D2BE] hover:underline">Mercedes Transmission Service</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/european-car-repair" className="text-[#00D2BE] hover:underline">All European Auto Repair Dallas</Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default MercedesService;
